/**
 * Batch review lock — cooperative multi-admin coordination for the
 * similarity triage page. Backed by /admin/bulk-import/batch/:id/lock,
 * heartbeat, DELETE.
 *
 * Return shape:
 *   status              'idle' | 'acquiring' | 'held' | 'blocked' | 'readonly' | 'stolen'
 *   activeLock          { user_id, user_name, acquired_at, expires_at, is_expired } | null
 *   error               human-readable message when acquire/heartbeat fails
 *   acquire(force)      manual (re)acquire — used by "Take over"
 *   enterReadOnly()     admin chose "Read-only view" from the gate
 *   release()           idempotent — called on unmount / beforeunload
 *
 * Lifecycle:
 *   1. mount             → acquire(force=false)
 *      • 200            → status='held', heartbeat interval starts
 *      • 423            → status='blocked', activeLock populated
 *   2. heartbeat (60s)   → refresh TTL
 *      • 200            → renew
 *      • 423            → status='stolen' (someone took over)
 *   3. takeover click    → acquire(force=true) → status='held', heartbeat restarts
 *   4. read-only click   → status='readonly'; no heartbeat; page renders data
 *                          but all mutation buttons are disabled
 *   5. unmount           → release() (best-effort — TTL is the backstop)
 *
 * The reason lock lives in a hook (not the giant BulkSimilarityPage
 * component) is that the page already has 39 useState calls and 26+
 * useEffect hooks — adding another 4 there would push the file past
 * the point of being reviewable. The hook keeps the coordination
 * concerns cleanly isolated + independently testable in isolation.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HEARTBEAT_MS = 60_000; // 60s — half of the 10-min server TTL

export default function useBatchReviewLock(batchId, token) {
  const [status, setStatus] = useState('idle');
  const [activeLock, setActiveLock] = useState(null);
  const [error, setError] = useState(null);
  const heartbeatRef = useRef(null);
  const releasedRef = useRef(false);

  const headers = { Authorization: `Bearer ${token}` };

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(async () => {
      try {
        const res = await axios.post(
          `${API}/admin/bulk-import/batch/${batchId}/lock/heartbeat`,
          {},
          { headers },
        );
        setActiveLock(res.data.lock);
      } catch (err) {
        if (err?.response?.status === 423) {
          // Someone took over — flip UI to read-only. The heartbeat
          // response body (in `detail`) carries the new holder so we
          // can render "X took over" without a second round-trip.
          const detail = err.response.data?.detail || err.response.data || {};
          setActiveLock(detail.lock || null);
          setStatus('stolen');
          stopHeartbeat();
        }
        // Any other error is transient — keep trying. Server TTL will
        // eventually kick us out anyway if the network stays down.
      }
    }, HEARTBEAT_MS);
  }, [batchId, token, stopHeartbeat]);

  const acquire = useCallback(
    async (force = false) => {
      if (!batchId || !token) return;
      setStatus('acquiring');
      setError(null);
      try {
        const res = await axios.post(
          `${API}/admin/bulk-import/batch/${batchId}/lock`,
          { force },
          { headers },
        );
        setActiveLock(res.data.lock);
        setStatus('held');
        releasedRef.current = false;
        startHeartbeat();
      } catch (err) {
        if (err?.response?.status === 423) {
          const detail = err.response.data?.detail || err.response.data || {};
          setActiveLock(detail.lock || null);
          setStatus('blocked');
        } else {
          // Backend down / auth failure / etc. Fail open — let the
          // admin work; the unique `id` index is the last-line defense.
          setError(err?.response?.data?.detail || err.message || 'Lock unavailable');
          setStatus('held');
          releasedRef.current = false;
          // Don't start heartbeat — nothing to refresh.
        }
      }
    },
    [batchId, token, startHeartbeat],
  );

  const enterReadOnly = useCallback(() => {
    stopHeartbeat();
    setStatus('readonly');
  }, [stopHeartbeat]);

  const release = useCallback(async () => {
    if (releasedRef.current || !batchId || !token) return;
    releasedRef.current = true;
    stopHeartbeat();
    try {
      await axios.delete(
        `${API}/admin/bulk-import/batch/${batchId}/lock`,
        { headers },
      );
    } catch (e) {
      // Best-effort. TTL will clean up.
      if (process.env.NODE_ENV !== 'production') console.warn('[ReviewLock] release failed:', e);
    }
  }, [batchId, token, stopHeartbeat]);

  // Mount: acquire. Unmount: release + stop heartbeat.
  useEffect(() => {
    acquire(false);

    // beforeunload — tab close / navigate away. Best-effort; browser
    // may kill the request before it lands, TTL is the backstop.
    const onBeforeUnload = () => {
      if (releasedRef.current || !batchId || !token) return;
      const url = `${API}/admin/bulk-import/batch/${batchId}/lock`;
      try {
        // navigator.sendBeacon is more reliable during unload but only
        // supports POST; keepalive fetch is the DELETE equivalent.
        fetch(url, {
          method: 'DELETE',
          headers,
          keepalive: true,
        });
      } catch {
        // Intentional no-op: fetch fires during page unload (keepalive).
        // The browser may kill execution before this catch runs; TTL is the backstop.
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      stopHeartbeat();
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, token]);

  // Read-only sub-states expose `canMutate` for cleaner callsite checks.
  const canMutate = status === 'held';

  return {
    status,
    activeLock,
    error,
    canMutate,
    acquire,
    enterReadOnly,
    release,
  };
}
